import { createQuery, Middleware, QueryHook } from 'react-query-kit';
import { get } from 'lodash';
import objectHash from 'object-hash';

import { STALE_TIME } from '@blockworks/platform/api';

import { parseStringError, VisualizationError, VisualizationErrorType, VisualizationErrorVersion } from '../models';
import { getUniqueGroupByValues, groupSeriesByColumn } from '../utils/group-series-by-column';
import { getVisualizationProps } from '../workflows';
import { getVisualizationPropsEnhanced } from '../workflows/get-visualization-props-enhanced';
import { validateExecution } from '../workflows/utils/validate-execution';

import { VisualizationWorkerApi } from './visualization-worker';
import { useWorker } from './visualization-worker.context';

const shimWorkerApi: VisualizationWorkerApi = {
    groupSeriesByColumn,
    getUniqueGroupByValues,
    getVisualizationProps,
    getVisualizationPropsEnhanced,
};

// TODO: Migrate this to accept to be less opinionated on validation steps - this will only work in cases where we assume execution is a variable key

const errorBoundary = (propsFunction?: (args: any) => any | Promise<any>) => async (props: any) => {
    try {
        validateExecution(props.execution);
    } catch (error) {
        console.warn('Error validating execution', error);

        if (error instanceof Error) {
            throw new VisualizationError({
                type: VisualizationErrorType.Execution,
                queryId: props.execution.queryId,
                visualizationId: props.id,
                executionId: props.execution.executionId,
                message: error.message,
            });
        }
    }

    if (!propsFunction) {
        console.warn('Error: Worker unavailable');

        throw new VisualizationError({
            type: VisualizationErrorType.Worker,
            message: 'Worker unavailable',
        });
    }

    try {
        const resultProps = await propsFunction(props);
        return resultProps;
    } catch (error) {
        if (error instanceof Error) {
            console.warn('Error getting visualization props', error);
            const parsedError = parseStringError(error.message);
            if (parsedError.version === VisualizationErrorVersion.V1) {
                throw new VisualizationError({
                    type: VisualizationErrorType.Configuration,
                    queryId: props.execution.queryId,
                    visualizationId: props.id,
                    executionId: props.execution.executionId,
                    message: parsedError.message,
                });
            } else {
                throw new VisualizationError({
                    type: VisualizationErrorType.Schema,
                    queryId: props.execution.queryId,
                    visualizationId: props.id,
                    executionId: props.execution.executionId,
                    message: 'Schema Error',
                    details: parsedError.errors,
                });
            }
        }
    }

    return null;
};

const visualizationPropsMiddleware =
    (method: keyof VisualizationWorkerApi): Middleware<QueryHook<any, any>> =>
    useNextQuery => {
        return options => {
            const worker = useWorker();
            const { variables } = options;
            let methodName = method;

            let nextVariables: Record<string, any> = {
                title: variables.title,
                id: variables.id,
                type: variables.type,
                nonce: get(variables, 'execution.rows.length'),
                executionId: get(variables, 'execution.executionId'),
                queryId: get(variables, 'execution.queryId'),
                params: get(variables, 'params'),
            };

            if (variables.editing) {
                // @TODO: Enable this through edge config later.
                methodName = 'getVisualizationPropsEnhanced';
                nextVariables = {
                    ...nextVariables,
                    editing: variables.editing,
                    config: objectHash(options.variables.config),
                };
            }

            return useNextQuery({
                ...options,
                staleTime: STALE_TIME.$5Minutes,
                variables: nextVariables,
                fetcher: async _ => {
                    if (!worker) {
                        return errorBoundary(shimWorkerApi[methodName])(options.variables);
                    }

                    return errorBoundary(worker[methodName])(options.variables);
                },
            });
        };
    };

const simpleWorkerMiddleware =
    (method: keyof VisualizationWorkerApi): Middleware<QueryHook<any, any>> =>
    useNextQuery => {
        return options => {
            const worker = useWorker();
            const { variables } = options;
            const nextVariables: Record<string, any> = {
                groupBy: variables.groupBy,
                executionId: get(variables, 'execution.executionId'),
                queryId: get(variables, 'execution.queryId'),
            };

            return useNextQuery({
                ...options,
                staleTime: STALE_TIME.$5Minutes,
                variables: nextVariables,
                fetcher: async _ => {
                    if (!worker) {
                        return errorBoundary(shimWorkerApi[method])(options.variables);
                    }
                    return errorBoundary(worker[method])(options.variables);
                },
            });
        };
    };

const useWorkerVisualizationProps = createQuery({
    queryKey: ['get-visualization-props'],
    fetcher: errorBoundary(getVisualizationProps),
    use: [visualizationPropsMiddleware('getVisualizationProps')],
});

const useWorkerGroupBy = createQuery({
    queryKey: ['group-series-by-column'],
    fetcher: shimWorkerApi.groupSeriesByColumn,
    use: [simpleWorkerMiddleware('groupSeriesByColumn')],
});

const useUniqueGroupByValues = createQuery({
    queryKey: ['get-discrete-group-by-columns'],
    fetcher: shimWorkerApi.getUniqueGroupByValues,
    use: [simpleWorkerMiddleware('getUniqueGroupByValues')],
});

export { useUniqueGroupByValues, useWorkerGroupBy, useWorkerVisualizationProps };
